import { generateCSSBreakpointMapForImages } from "@faire/retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Banner/ByType/utils";
import { Context } from "@faire/retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Context";
import { EAEAEA_PIXEL } from "@faire/retailer-visitor-shared/consts/EAEAEA_PIXEL";
import { optimizeImageUrl } from "@faire/web--source/common/images/optimizeImages";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { Row } from "@faire/web--source/slate/Layout";
import { getSpacing } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { useResponsiveValue } from "@faire/web--source/ui/hooks/useResponsiveValue";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import React from "react";
import styled from "styled-components";

const DEFAULT_MOBILE_IMAGE_WIDTH = 375;
const MOBILE_BANNER_IMAGE_HEIGHT = 200;
const TABLET_BANNER_IMAGE_HEIGHT = 240;
const DESKTOP_BANNER_IMAGE_HEIGHT = 320;
const MAX_BANNER_WIDTH = 1920;

export const FullWidthImageBanner: React.FC = () => {
  const { layoutElement } = React.useContext(Context);
  const images = generateCSSBreakpointMapForImages(layoutElement.images);
  const isSSR = useIsSSR();
  const { isMobile } = useViewport();

  const mobileImage = images.mobile;

  const backgroundImage = useResponsiveValue({
    mobileAndAbove: mobileImage,
    tabletAndAbove: images.tablet,
    desktopAndAbove: images.desktop,
    xLargeAndAbove: images.xlarge,
  });

  const defaultBannerImageHeight = useResponsiveValue({
    mobileAndAbove: mobileImage?.height ?? MOBILE_BANNER_IMAGE_HEIGHT,
    tabletAndAbove: TABLET_BANNER_IMAGE_HEIGHT,
    desktopAndAbove: DESKTOP_BANNER_IMAGE_HEIGHT,
  });

  const layoutImageHeight = backgroundImage?.height ?? defaultBannerImageHeight;

  const mobileAspectRatio =
    (mobileImage?.width ?? DEFAULT_MOBILE_IMAGE_WIDTH) /
    (mobileImage?.height ?? MOBILE_BANNER_IMAGE_HEIGHT);

  return (
    <FullWidthImageWrapper
      $height={layoutImageHeight}
      $mobileAspectRatio={mobileAspectRatio}
    >
      <Image
        src={optimizeImageUrl(backgroundImage ?? { url: EAEAEA_PIXEL }, isSSR, {
          height: layoutImageHeight,
        })}
      />
      {layoutElement.main_text ? (
        <StyledHeader
          color={layoutElement.main_text_color}
          variant={isMobile ? "displaySSerifRegular" : "displayLSerifRegular"}
          align="center"
        >
          {layoutElement.main_text}
        </StyledHeader>
      ) : null}
    </FullWidthImageWrapper>
  );
};

const FullWidthImageWrapper = styled(Row).attrs({
  align: "center",
  justify: "center",
})<{ $height: number; $mobileAspectRatio: number }>`
  position: relative;
  width: 100%;
  padding: ${getSpacing(12)};

  ${responsiveMediaQueries.tabletAndAbove} {
    height: ${({ $height }) => $height}px;
    padding: 0;
    max-width: ${MAX_BANNER_WIDTH}px;
    margin: 0 auto;
  }

  ${responsiveMediaQueries.mobile} {
    aspect-ratio: ${({ $mobileAspectRatio }) => $mobileAspectRatio};
  }
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  position: absolute;
  top: 0;
  left: 0;
`;

const StyledHeader = styled(Typography)`
  z-index: 1;
`;
